import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryClient, queryKeys } from './query-client';
import { prefetchReviewOnce, REVIEW_FRESH_MS, reviewImageUrls } from './review-prefetch';
import type { BlogDraftPreview } from './blog-draft-preview';

class BlogDraftReadFailed extends Error {
  constructor(readonly status: number) {
    super(`blog-draft-preview ${status}`);
  }
}

export type BlogDraftPreviewView = 'desktop' | 'mobile';

export async function fetchBlogDraftPreview(
  workspaceId: string,
  metaSiteId: string,
  draftPostId: string,
  view?: BlogDraftPreviewView,
): Promise<BlogDraftPreview> {
  const query = new URLSearchParams({ workspaceId, metaSiteId, draftPostId, ...(view ? { view } : {}) });
  const res = await apiFetch(`${API_BASE}/api/blog-draft-preview?${query.toString()}`, { credentials: 'include' });
  if (!res.ok) throw new BlogDraftReadFailed(res.status);
  const body = (await res.json().catch(() => null)) as Partial<BlogDraftPreview> | null;
  if (!body || typeof body !== 'object') throw new BlogDraftReadFailed(res.status);
  return {
    title: body.title ?? '',
    html: body.html ?? '',
    heroImageUrl: body.heroImageUrl ?? '',
    sitePreviewUrl: typeof body.sitePreviewUrl === 'string' ? body.sitePreviewUrl : '',
  };
}

type BlogDraftIds = { workspaceId: string; metaSiteId: string; draftPostId: string };

const retryDraftRead = (count: number, error: unknown) =>
  count < 1 && !(error instanceof BlogDraftReadFailed && error.status >= 400 && error.status < 500);

const SITE_PREVIEW_FRESH_MS = 3 * 60 * 60_000;

function blogDraftReviewQuery(ids: BlogDraftIds, view: BlogDraftPreviewView, revision = '') {
  return {
    queryKey: queryKeys.blogDraftPreview(ids.workspaceId, ids.metaSiteId, ids.draftPostId, view, revision),
    queryFn: () => fetchBlogDraftPreview(ids.workspaceId, ids.metaSiteId, ids.draftPostId, view),
  };
}

export function useBlogDraftPreview(ids: BlogDraftIds, view: BlogDraftPreviewView, enabled = true, revision?: string) {
  return useQuery({
    ...blogDraftReviewQuery(ids, view, revision),
    enabled,
    staleTime: revision ? REVIEW_FRESH_MS : 0,
    retry: retryDraftRead,
  });
}

export function prefetchBlogDraftReview(ids: BlogDraftIds, view: BlogDraftPreviewView, revision: string): void {
  if (!revision || !ids.workspaceId || !ids.metaSiteId || !ids.draftPostId) return;
  prefetchReviewOnce(queryClient, blogDraftReviewQuery(ids, view, revision), (data) => reviewImageUrls(data.html, [data.heroImageUrl]));
}

export function useBlogDraftInlinePreview(ids: BlogDraftIds, cardKey: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.blogDraftInlinePreview(ids.workspaceId, ids.metaSiteId, ids.draftPostId, cardKey),
    queryFn: () => fetchBlogDraftPreview(ids.workspaceId, ids.metaSiteId, ids.draftPostId),
    enabled,
    staleTime: SITE_PREVIEW_FRESH_MS,
    cacheTime: SITE_PREVIEW_FRESH_MS,
    refetchOnWindowFocus: true,
    retry: retryDraftRead,
  });
}
